import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ApiError,
  getUserFacingApiMessage,
  getLocalUserFacingApiMessage,
  passThroughMessage,
} from "@/lib/api-client";
import { Sentry } from "@/lib/sentry";
import {
  createOrderErrorMessage,
  isRecoverableOrderCreateError,
  readCreateOrderUnavailable,
} from "@/features/marketplace/lib/order-errors";
import { isOrderLineVideoSlotEmpty } from "@/features/marketplace/lib/order-line-videos";
import {
  patchOrderAfterVideoSlotAdded,
  patchOrderAfterVideoSlotRemoved,
} from "@/features/marketplace/lib/order-video-cache-patch";
import type { OrderScriptRow } from "@/features/marketplace/types/marketplace.types";
import i18n from "@/lib/i18n";
import { orderStatusTimeKeys } from "@/lib/queries/order-status-time.queries";
import { scriptDeliveryKeys } from "@/lib/queries/script-delivery.queries";
import {
  fetchVendors,
  fetchVendorDetails,
  fetchConnections,
  createConnection,
  updateAnswers,
  resignConnection,
  disconnectVendor,
  fetchOrders,
  fetchOrder,
  fetchSentScripts,
  fetchOrderHandoffDefaults,
  previewOrderHandoffEmail,
  createOrder,
  retryOrder,
  updateOrderDueDate,
  updateOrderEta,
  updateOrderFulfillment,
  fetchAdminOrders,
  fetchAdminOrderVendors,
  updateOrderScriptFulfillment,
  addAdminOrderScriptVideoSlot,
  updateOrderScriptVideoLabel,
  deleteAdminOrderScriptVideo,
  duplicateOrderScript,
  fetchAdminOrderPartnerOptions,
  fetchAdminOrderScriptAdPlatforms,
  sendOrderScriptToPartner,
  fetchPartnerOrders,
  uploadPartnerPreflight,
  enablePartnerLogin,
  createOpportunityCustomBrief,
  fetchOrderScriptPreflightComments,
  fetchTenantOrderScriptPreflightComments,
  fetchPartnerOrderScriptPreflightComments,
  fetchMarketplaceOrderScriptByScriptReviewId,
  addOrderScriptPreflightComment,
  addTenantOrderScriptPreflightComment,
  addPartnerOrderScriptNote,
  setAdminOrderNoteReaction,
  setClientOrderNoteReaction,
} from "@/features/marketplace/api/marketplace.api";
import type {
  CreateConnectionPayload,
  UpdateAnswersPayload,
  ResignConnectionPayload,
  CreateOrderPayload,
  OrderHandoffEmailPreviewPayload,
  MarketplaceFulfillmentStatus,
  MarketplaceOrder,
  AdminOrdersPageResponse,
  AdminOrdersFilters,
  TenantOrdersFilters,
  TenantOrdersPageResponse,
  PartnerOrdersFilter,
  PartnerOrdersQueryFilters,
  UpdateOrderScriptFulfillmentPayload,
  SendOrderScriptToPartnerPayload,
  CreateCustomBriefPayload,
  MarketplaceOrderScriptByReviewLink,
  OrderScriptActivityAudience,
} from "@/features/marketplace/types/marketplace.types";
import {
  fetchAdminVendors,
  fetchAdminVendor,
  createAdminVendor,
  replaceAdminVendor,
  activateAdminVendor,
  approveVendorContactEmail,
  deactivateAdminVendor,
  revokeVendorContactEmail,
  fetchPendingVendors,
  fetchPendingOrderHandoffs,
  linkPendingVendor,
  rejectPendingVendor,
  retryAdminOrderHandoff,
} from "@/features/admin/marketplace/api/admin-vendors.api";
import {
  fetchTeamRecipients,
  createTeamRecipient,
  updateTeamRecipient,
  deleteTeamRecipient,
} from "@/features/admin/marketplace/api/team-recipients.api";
import {
  acceptOrderImportWarnings,
  acceptPrefilledOrderImportDates,
  bulkSetOrderImportClientStartDates,
  bulkSetOrderImportNewVendorMapping,
  bulkSetOrderImportVendorMapping,
  commitOrderImport,
  fetchOrderImportPreview,
  parseOrderImportSpreadsheet,
  updateOrderImportRow,
} from "@/features/admin/marketplace/api/admin-order-import.api";
import {
  canonicalTargetRatios,
  type CreativeImportPreviewDto,
  type TargetRatio,
} from "@nexus/contracts";
import { normalizeImportPreview } from "@/features/marketplace/admin/components/order-import/import-readiness";

import type { AdminVendorWritePayload } from "@/features/admin/marketplace/types/admin-marketplace.types";
import type {
  TeamRecipientCreatePayload,
  TeamRecipientUpdatePayload,
} from "@/features/admin/marketplace/types/admin-marketplace.types";
import type {
  BulkSetClientStartDateBody,
  BulkSetVendorMappingBody,
  BulkSetNewVendorMappingBody,
  UpdateCreativeImportRowBody,
} from "@nexus/contracts";
import { opportunityKeys } from "./opportunities.queries";
import { personaOpportunityHubKeys } from "./persona-opportunity-hub.queries";
import { invalidatePersonaOpportunitySets } from "./persona-opportunity-sets.queries";

import {
  inFlightSentScriptsRefetchInterval,
  invalidateOrderScriptPreflightCommentQueries,
  marketplaceKeys,
  MARKETPLACE_STALE_TIME_MS,
  inFlightHandoffRefetchInterval,
  invalidateMarketplaceOrders,
} from "./marketplace-query-keys";
import { useOrderNoteReactionMutation } from "./marketplace-reactions";

const TENANT_ORDERS_PAGE_SIZE = 50;

export function ordersQueryOptions(
  tenantId: string | null | undefined,
  filters: TenantOrdersFilters = {},
) {
  const tid = tenantId ?? "";
  const normalized: TenantOrdersFilters = {
    ...filters,
    limit: filters.limit ?? TENANT_ORDERS_PAGE_SIZE,
    offset: filters.offset ?? 0,
  };
  return queryOptions({
    queryKey: [...marketplaceKeys.orders(tid || "__none__"), normalized],
    queryFn: () => fetchOrders(tid, normalized),
    enabled: !!tid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
    refetchInterval: inFlightHandoffRefetchInterval,
  });
}

export function orderQueryOptions(
  tenantId: string | null | undefined,
  orderId: string | null | undefined,
  fulfillmentGroup?: TenantOrdersFilters["fulfillmentGroup"],
) {
  const tid = tenantId ?? "";
  const id = orderId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.order(
      tid || "__none__",
      id || "__none__",
      fulfillmentGroup,
    ),
    queryFn: () => fetchOrder(tid, id, fulfillmentGroup),
    enabled: !!tid && !!id,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function ordersForSetQueryOptions(
  tenantId: string | null | undefined,
  opportunitySetId: string | null | undefined,
) {
  const tid = tenantId ?? "";
  const setId = opportunitySetId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.ordersForSet(
      tid || "__none__",
      setId || "__none__",
    ),
    queryFn: () => fetchOrders(tid, { opportunitySetId: setId, limit: 100, offset: 0 }),
    enabled: !!tid && !!setId,
    staleTime: MARKETPLACE_STALE_TIME_MS,
    refetchInterval: inFlightHandoffRefetchInterval,
  });
}

/**
 * Map of scriptId → sent metadata for the active tenant. The Scripts tab
 * subscribes once on mount and renders badges synchronously from the cache.
 * Order-creation mutations invalidate this key so badges appear immediately.
 */
export function sentScriptsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.sentScripts(tid || "__none__"),
    queryFn: () => fetchSentScripts(tid),
    enabled: !!tid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
    refetchInterval: inFlightSentScriptsRefetchInterval,
  });
}

export function orderHandoffDefaultsQueryOptions(
  tenantId: string | null | undefined,
  tenantVendorId: string | null | undefined,
) {
  const tid = tenantId ?? "";
  const connectionId = tenantVendorId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.orderHandoffDefaults(
      tid || "__none__",
      connectionId || "__none__",
    ),
    queryFn: () => fetchOrderHandoffDefaults(tid, connectionId),
    enabled: !!tid && !!connectionId,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function orderHandoffEmailPreviewQueryOptions(
  payload: OrderHandoffEmailPreviewPayload,
) {
  return queryOptions({
    queryKey: marketplaceKeys.orderHandoffEmailPreview(payload),
    queryFn: () => previewOrderHandoffEmail(payload),
    enabled: Boolean(
      payload.tenantId &&
        payload.tenantVendorId &&
        (payload.scriptIds.length > 0 || (payload.briefIds?.length ?? 0) > 0),
    ),
  });
}


/** `toastErrors: false` when the caller shows the failure itself, e.g. one result row per partner. */
export function useCreateOrder({ toastErrors = true }: { toastErrors?: boolean } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOrderPayload) => createOrder(payload),
    onSuccess: (_data, payload) => {
      invalidateMarketplaceOrders(queryClient, payload.tenantId);
      invalidatePersonaOpportunitySets(queryClient, payload.tenantId);
      void queryClient.invalidateQueries({ queryKey: personaOpportunityHubKeys.all });
    },
    onError: (err) => {
      if (toastErrors) toast.error(createOrderErrorMessage(err, i18n.t));
    },
    meta: { skipGlobalError: true },
  });
}

export function useRetryOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { orderId: string; tenantId: string }) =>
      retryOrder(args.orderId, args.tenantId),
    onSuccess: (_data, args) => {
      invalidateMarketplaceOrders(queryClient, args.tenantId);
      toast.success("Handoff retry queued");
    },
    onError: () => {
      toast.error("Couldn't retry this order");
    },
    meta: { skipGlobalError: true },
  });
}

function etaAttemptFingerprint(
  etaDate: string | null,
  etaNote: string | null,
): string {
  return JSON.stringify({ etaDate, etaNote });
}

export function useUpdateOrderEta() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const attemptKeys = useRef(
    new Map<string, { key: string; fingerprint: string }>(),
  );
  return useMutation({
    mutationFn: (args: {
      orderId: string;
      tenantId: string;
      etaDate: string | null;
      etaNote: string | null;
    }) => {
      const fingerprint = etaAttemptFingerprint(args.etaDate, args.etaNote);
      const existing = attemptKeys.current.get(args.orderId);
      const idempotencyKey =
        existing?.fingerprint === fingerprint
          ? existing.key
          : crypto.randomUUID();
      attemptKeys.current.set(args.orderId, {
        key: idempotencyKey,
        fingerprint,
      });
      return updateOrderEta(args.orderId, {
        tenantId: args.tenantId,
        etaDate: args.etaDate,
        etaNote: args.etaNote,
        idempotencyKey,
      });
    },
    onSuccess: (result, args) => {
      attemptKeys.current.delete(args.orderId);
      patchOrderInAdminListCache(queryClient, result.order);
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.orders(args.tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminOrdersRoot,
      });
      if (result.emailWarning) {
        toast.warning(
          t(
            "marketplace.orders.eta.ccEmptyWarning",
            "ETA saved. This order has no CC recipients, so no email was sent.",
          ),
        );
      }
    },
  });
}

function dueDateAttemptTarget(args: {
  orderId: string;
  orderScriptId: string | null;
}): string {
  return `${args.orderId}:${args.orderScriptId ?? ""}`;
}

function dueDateAttemptFingerprint(dueDate: string | null): string {
  return JSON.stringify({ dueDate });
}

/**
 * Move the deadline the ops tracker filters on — one script, or the order ETA
 * plus every open script due earlier. Reuses the key while the payload is
 * unchanged so a timeout retry replays instead of re-sending email/Slack.
 */
export function useUpdateOrderDueDate() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const attemptKeys = useRef(
    new Map<string, { key: string; fingerprint: string }>(),
  );
  return useMutation({
    mutationFn: (args: {
      orderId: string;
      tenantId: string;
      orderScriptId: string | null;
      dueDate: string | null;
    }) => {
      const target = dueDateAttemptTarget(args);
      const fingerprint = dueDateAttemptFingerprint(args.dueDate);
      const existing = attemptKeys.current.get(target);
      const idempotencyKey =
        existing?.fingerprint === fingerprint
          ? existing.key
          : crypto.randomUUID();
      attemptKeys.current.set(target, { key: idempotencyKey, fingerprint });
      return updateOrderDueDate(args.orderId, {
        tenantId: args.tenantId,
        orderScriptId: args.orderScriptId,
        dueDate: args.dueDate,
        idempotencyKey,
      });
    },
    onSuccess: (result, args) => {
      const target = dueDateAttemptTarget(args);
      if (
        attemptKeys.current.get(target)?.fingerprint ===
        dueDateAttemptFingerprint(args.dueDate)
      ) {
        attemptKeys.current.delete(target);
      }
      patchOrderInAdminListCache(queryClient, result.order);
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.orders(args.tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminOrdersRoot,
      });
      if (result.emailWarning) {
        toast.warning(
          t(
            "marketplace.orders.eta.ccEmptyWarning",
            "ETA saved. This order has no CC recipients, so no email was sent.",
          ),
        );
      }
    },
  });
}

function invalidateDeliveryStatistics(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: scriptDeliveryKeys.all });
  void queryClient.invalidateQueries({ queryKey: orderStatusTimeKeys.all });
}

export function useUpdateOrderFulfillment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      orderId: string;
      tenantId: string;
      fulfillmentStatus: MarketplaceFulfillmentStatus;
    }) =>
      updateOrderFulfillment(
        args.orderId,
        args.tenantId,
        args.fulfillmentStatus,
      ),
    onSuccess: (data, args) => {
      patchOrderInTenantListCache(queryClient, args.tenantId, data);
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminOrdersRoot,
      });
      invalidateDeliveryStatistics(queryClient);
    },
    onError: () => {
      toast.error(
        i18n.t(
          "marketplace.orders.list.fulfillmentStatusError",
          "Couldn't update fulfilment status",
        ),
      );
    },
    meta: { skipGlobalError: true },
  });
}

const ADMIN_ORDERS_PAGE_SIZE = 20;

export function adminOrdersQueryOptions(filters: AdminOrdersFilters = {}) {
  const normalized: AdminOrdersFilters = {
    ...filters,
    limit: filters.limit ?? ADMIN_ORDERS_PAGE_SIZE,
    offset: filters.offset ?? 0,
  };
  return queryOptions({
    queryKey: marketplaceKeys.adminOrders(normalized),
    queryFn: () => fetchAdminOrders(normalized),
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function adminOrderVendorsQueryOptions() {
  return queryOptions({
    queryKey: marketplaceKeys.adminOrderVendors,
    queryFn: fetchAdminOrderVendors,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function adminOrderPartnerOptionsQueryOptions(orderId: string) {
  return queryOptions({
    queryKey: marketplaceKeys.adminOrderPartnerOptions(orderId),
    queryFn: () => fetchAdminOrderPartnerOptions(orderId),
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function adminOrderScriptAdPlatformsQueryOptions(orderId: string, orderScriptId: string) {
  return queryOptions({
    queryKey: marketplaceKeys.adminOrderScriptAdPlatforms(orderId, orderScriptId),
    queryFn: () => fetchAdminOrderScriptAdPlatforms(orderId, orderScriptId),
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function adminOrdersSummaryQueryOptions(
  filters: Pick<AdminOrdersFilters, "clientTenantId" | "clientTenantIds"> = {},
) {
  const queryFilters: AdminOrdersFilters = {
    limit: 1,
    offset: 0,
    includeSummary: true,
    ...(filters.clientTenantId
      ? { clientTenantId: filters.clientTenantId }
      : {}),
    ...(filters.clientTenantIds?.length
      ? { clientTenantIds: filters.clientTenantIds }
      : {}),
  };
  return queryOptions({
    queryKey: marketplaceKeys.adminOrders(queryFilters),
    queryFn: () => fetchAdminOrders(queryFilters),
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function useAdminOrders(filters: AdminOrdersFilters = {}) {
  return useQuery(adminOrdersQueryOptions(filters));
}

const scriptFulfillmentQueues = new Map<string, Promise<unknown>>();

function enqueueOrderScriptFulfillment<T>(
  orderId: string,
  work: () => Promise<T>,
): Promise<T> {
  const previous = scriptFulfillmentQueues.get(orderId) ?? Promise.resolve();
  const next = previous
    .catch(() => undefined)
    .then(work)
    .finally(() => {
      if (scriptFulfillmentQueues.get(orderId) === next) {
        scriptFulfillmentQueues.delete(orderId);
      }
    });
  scriptFulfillmentQueues.set(orderId, next);
  return next;
}

function patchOrderInTenantListCache(
  queryClient: ReturnType<typeof useQueryClient>,
  tenantId: string,
  updated: MarketplaceOrder,
) {
  queryClient.setQueriesData<TenantOrdersPageResponse>(
    { queryKey: marketplaceKeys.orders(tenantId) },
    (current) => {
      if (!current?.orders) return current;
      return {
        ...current,
        orders: current.orders.map((order) =>
          order.id === updated.id ? updated : order,
        ),
      };
    },
  );
}

function patchScriptFulfillmentOptimistic(
  script: OrderScriptRow,
  fulfillmentStatus: MarketplaceFulfillmentStatus,
  deliverableId?: string,
): OrderScriptRow {
  if (!script.videos?.length) {
    return { ...script, fulfillmentStatus };
  }
  const applyToAllVideos = deliverableId == null;
  const videos = script.videos.map((video) => {
    const slotFilled = !isOrderLineVideoSlotEmpty(video);
    let reviewStatus = video.reviewStatus;
    let revisionNote = video.revisionNote;
    const targetsThisVideo =
      applyToAllVideos || (deliverableId != null && video.id === deliverableId);
    if (!targetsThisVideo || !slotFilled) {
      return video;
    }
    if (fulfillmentStatus === "SENT_FOR_REVISION") {
      reviewStatus = "REVISION_REQUESTED";
    } else {
      reviewStatus = "IN_REVIEW";
      revisionNote = null;
    }
    const patched = { ...video, reviewStatus, revisionNote };
    return {
      ...patched,
      fulfillmentStatus: fulfillmentStatus,
    };
  });
  return { ...script, fulfillmentStatus, videos };
}

function patchOrderInAdminListCache(
  queryClient: ReturnType<typeof useQueryClient>,
  updated: MarketplaceOrder,
) {
  queryClient.setQueriesData<AdminOrdersPageResponse>(
    { queryKey: marketplaceKeys.adminOrdersRoot },
    (current) => {
      if (!current) return current;
      return {
        ...current,
        orders: current.orders.map((order) =>
          order.id === updated.id
            ? { ...order, ...updated, tenant: order.tenant }
            : order,
        ),
      };
    },
  );
}

export function useUpdateOrderScriptFulfillment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      orderId: string;
      orderScriptId: string;
      tenantId: string;
      fulfillmentStatus: MarketplaceFulfillmentStatus;
      revisionNote?: string | null;
      deliverableId?: string;
      admin?: boolean;
      suppressErrorToast?: boolean;
    }) =>
      enqueueOrderScriptFulfillment(args.orderId, () =>
        updateOrderScriptFulfillment(
          args.orderId,
          args.orderScriptId,
          {
            tenantId: args.tenantId,
            fulfillmentStatus: args.fulfillmentStatus,
            revisionNote: args.revisionNote,
            deliverableId: args.deliverableId,
          },
          { admin: args.admin },
        ),
      ),
    onMutate: async (args) => {
      await queryClient.cancelQueries({
        queryKey: marketplaceKeys.orders(args.tenantId),
      });
      await queryClient.cancelQueries({
        queryKey: marketplaceKeys.adminOrdersRoot,
      });
      const previous = queryClient.getQueriesData<TenantOrdersPageResponse>({
        queryKey: marketplaceKeys.orders(args.tenantId),
      });
      const previousAdmin = queryClient.getQueriesData<AdminOrdersPageResponse>({
        queryKey: marketplaceKeys.adminOrdersRoot,
      });
      const patchOrder = (order: MarketplaceOrder) => {
        if (order.id !== args.orderId) return order;
        return {
          ...order,
          scripts: order.scripts.map((script) =>
            script.id === args.orderScriptId
              ? patchScriptFulfillmentOptimistic(
                  script,
                  args.fulfillmentStatus,
                  args.deliverableId,
                )
              : script,
          ),
        };
      };
      queryClient.setQueriesData<TenantOrdersPageResponse>(
        { queryKey: marketplaceKeys.orders(args.tenantId) },
        (current) => {
          if (!current?.orders) return current;
          return {
            ...current,
            orders: current.orders.map(patchOrder),
          };
        },
      );
      queryClient.setQueriesData<AdminOrdersPageResponse>(
        { queryKey: marketplaceKeys.adminOrdersRoot },
        (current) => {
          if (!current?.orders) return current;
          return {
            ...current,
            orders: current.orders.map((order) => ({
              ...order,
              ...patchOrder(order),
              tenant: order.tenant,
            })),
          };
        },
      );
      return { previous, previousAdmin };
    },
    onSuccess: (data, args) => {
      patchOrderInAdminListCache(queryClient, data);
      patchOrderInTenantListCache(queryClient, args.tenantId, data);
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminOrdersRoot,
      });
      queryClient.invalidateQueries({
        queryKey: ["marketplace", "partner", "orders"],
      });
      invalidateDeliveryStatistics(queryClient);
      invalidateOrderScriptPreflightCommentQueries(queryClient, {
        audiences: ["admin", "client"],
        tenantScope: args.tenantId,
        orderId: args.orderId,
        orderScriptId: args.orderScriptId,
        deliverableId: args.deliverableId,
      });
    },
    onError: (_error, args, context) => {
      if (context?.previous) {
        for (const [queryKey, data] of context.previous) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousAdmin) {
        for (const [queryKey, data] of context.previousAdmin) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (!args.suppressErrorToast) {
        toast.error(
          i18n.t(
            "marketplace.orders.list.scriptStatusError",
            "Couldn't update script fulfilment status",
          ),
        );
      }
    },
    meta: { skipGlobalError: true },
  });
}

/**
 * Copies a sent persona script into the client's Your Scripts. A line keeps
 * one idempotency key until a copy succeeds, so double-clicks and retries of
 * the same intent replay one copy instead of minting several.
 */
export function useAddAdminOrderScriptVideoSlot() {
  const queryClient = useQueryClient();
  const attemptKeys = useRef(new Map<string, string>());
  return useMutation({
    mutationFn: (args: { orderId: string; orderScriptId: string; label?: string | null }) => {
      const attempt = `${args.orderId}:${args.orderScriptId}:video-slot`;
      const idempotencyKey = attemptKeys.current.get(attempt) ?? crypto.randomUUID();
      attemptKeys.current.set(attempt, idempotencyKey);
      return addAdminOrderScriptVideoSlot(args.orderId, args.orderScriptId, {
        idempotencyKey,
        label: args.label,
      });
    },
    onSuccess: (slot, args) => {
      attemptKeys.current.delete(`${args.orderId}:${args.orderScriptId}:video-slot`);
      queryClient.setQueriesData<AdminOrdersPageResponse>(
        { queryKey: marketplaceKeys.adminOrdersRoot },
        (current) => {
          if (!current?.orders) return current;
          return {
            ...current,
            orders: current.orders.map((order) =>
              order.id === args.orderId
                ? {
                    ...patchOrderAfterVideoSlotAdded(
                      order,
                      args.orderScriptId,
                      slot,
                    ),
                    tenant: order.tenant,
                  }
                : order,
            ),
          };
        },
      );
      toast.success(
        i18n.t(
          "marketplace.admin.orders.videoSlotAdded",
          "Video slot added",
        ),
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(
          i18n.t(
            "marketplace.admin.orders.videoSlotInProgress",
            "A video slot is already being added. Wait a moment and try again.",
          ),
        );
        return;
      }
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useUpdateOrderScriptVideoLabel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      orderId: string;
      orderScriptId: string;
      deliverableId: string;
      label: string | null;
      admin?: boolean;
    }) =>
      updateOrderScriptVideoLabel(
        args.orderId,
        args.orderScriptId,
        args.deliverableId,
        args.label,
        { admin: args.admin },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminOrdersRoot,
      });
      queryClient.invalidateQueries({
        queryKey: ["marketplace", "partner", "orders"],
      });
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useDeleteAdminOrderScriptVideo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      orderId: string;
      orderScriptId: string;
      deliverableId: string;
    }) =>
      deleteAdminOrderScriptVideo(
        args.orderId,
        args.orderScriptId,
        args.deliverableId,
      ),
    onSuccess: (data, args) => {
      queryClient.setQueriesData<AdminOrdersPageResponse>(
        { queryKey: marketplaceKeys.adminOrdersRoot },
        (current) => {
          if (!current?.orders) return current;
          return {
            ...current,
            orders: current.orders.map((order) =>
              order.id === args.orderId
                ? {
                    ...patchOrderAfterVideoSlotRemoved(
                      order,
                      args.orderScriptId,
                      args.deliverableId,
                    ),
                    tenant: order.tenant,
                  }
                : order,
            ),
          };
        },
      );
      if (data.tenantId) {
        patchOrderInTenantListCache(queryClient, data.tenantId, data);
      }
      queryClient.invalidateQueries({
        queryKey: ["marketplace", "partner", "orders"],
      });
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useDuplicateOrderScript() {
  const queryClient = useQueryClient();
  const attemptKeys = useRef(new Map<string, string>());
  return useMutation({
    mutationFn: (args: { orderId: string; orderScriptId: string; tenantId: string }) => {
      const idempotencyKey =
        attemptKeys.current.get(args.orderScriptId) ?? crypto.randomUUID();
      attemptKeys.current.set(args.orderScriptId, idempotencyKey);
      return duplicateOrderScript(args.orderId, args.orderScriptId, idempotencyKey);
    },
    onSuccess: (_data, args) => {
      attemptKeys.current.delete(args.orderScriptId);
      invalidatePersonaOpportunitySets(queryClient, args.tenantId);
      void queryClient.invalidateQueries({ queryKey: personaOpportunityHubKeys.all });
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

/**
 * Copies a sent line and places a new order for another partner of the same
 * client. Each (line, partner, note, ratios) keeps one idempotency key until a send
 * succeeds — across dialog closes — so resending after a timeout or 503
 * replays the order the server may already have created instead of placing
 * a second one. The note is part of the identity because the server rejects a
 * replay whose note differs, so an edited note is a new attempt. The picked
 * ratios are part of it too: a replay returns the first order unchanged, so
 * reusing the key after the ratios change would silently keep the old ones. A 503 that
 * names the persisted order gets no toast here; the caller owns that message
 * so it can link to the order.
 */
export function useSendOrderScriptToPartner() {
  const queryClient = useQueryClient();
  const attemptKeys = useRef(new Map<string, string>());
  return useMutation({
    mutationFn: (args: {
      orderId: string;
      orderScriptId: string;
      tenantId: string;
      payload: Omit<SendOrderScriptToPartnerPayload, "idempotencyKey">;
    }) => {
      const attempt = sendAttemptId(args.orderScriptId, args.payload);
      const idempotencyKey = attemptKeys.current.get(attempt) ?? crypto.randomUUID();
      attemptKeys.current.set(attempt, idempotencyKey);
      return sendOrderScriptToPartner(args.orderId, args.orderScriptId, {
        ...args.payload,
        idempotencyKey,
      });
    },
    onSuccess: (_data, args) => {
      attemptKeys.current.delete(sendAttemptId(args.orderScriptId, args.payload));
      invalidateAfterPartnerSend(queryClient, args.tenantId);
    },
    onError: (err, args) => {
      if (readCreateOrderUnavailable(err)) {
        invalidateAfterPartnerSend(queryClient, args.tenantId);
        return;
      }
      toast.error(
        isRecoverableOrderCreateError(err)
          ? i18n.t(
              "marketplace.orders.sendToPartner.unconfirmed",
              "We couldn't confirm the send. Sending again to the same partner with the same note and target ratios is safe — it won't create a second order.",
            )
          : createOrderErrorMessage(err, i18n.t),
      );
    },
    meta: { skipGlobalError: true },
  });
}

function sendAttemptId(
  orderScriptId: string,
  payload: {
    tenantVendorId: string;
    handoffOverrideNote?: string;
    targetRatios?: TargetRatio[];
  },
): string {
  return JSON.stringify([
    orderScriptId,
    payload.tenantVendorId,
    payload.handoffOverrideNote?.trim() ?? "",
    canonicalTargetRatios(payload.targetRatios ?? []),
  ]);
}

/** A send that saved its order, even one whose handoff 503'd, also saved the copied script. */
function invalidateAfterPartnerSend(queryClient: QueryClient, tenantId: string): void {
  invalidateMarketplaceOrders(queryClient, tenantId);
  invalidatePersonaOpportunitySets(queryClient, tenantId);
  void queryClient.invalidateQueries({ queryKey: personaOpportunityHubKeys.all });
}

export function useCreateCustomBriefScript() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { setId: string; payload: CreateCustomBriefPayload }) =>
      createOpportunityCustomBrief(args.setId, args.payload),
    onSuccess: (_data, args) => {
      void queryClient.invalidateQueries({
        queryKey: opportunityKeys.scripts(args.setId),
      });
      toast.success(
        i18n.t("marketplace.customBrief.success", "Custom brief added"),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}


export function marketplaceOrderScriptByReviewQueryOptions(
  scriptReviewId: string,
) {
  return queryOptions({
    queryKey: [
      ...marketplaceKeys.all,
      "order-script-by-review",
      scriptReviewId,
    ] as const,
    queryFn: () => fetchMarketplaceOrderScriptByScriptReviewId(scriptReviewId),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });
}

export function useMarketplaceOrderScriptByReview(
  scriptReviewId: string,
  enabled = true,
) {
  return useQuery({
    ...marketplaceOrderScriptByReviewQueryOptions(scriptReviewId),
    enabled,
  });
}

export function orderScriptPreflightCommentsQueryOptions(
  orderId: string,
  orderScriptId: string,
  options: {
    audience: Exclude<OrderScriptActivityAudience, "partner">;
    tenantId: string;
    deliverableId?: string;
  },
) {
  const { audience, tenantId, deliverableId } = options;
  return queryOptions({
    queryKey: marketplaceKeys.preflightComments(
      audience,
      tenantId,
      orderId,
      orderScriptId,
      deliverableId,
    ),
    queryFn: () =>
      audience === "client" && tenantId
        ? fetchTenantOrderScriptPreflightComments(
            orderId,
            orderScriptId,
            tenantId,
            deliverableId,
          )
        : fetchOrderScriptPreflightComments(
            orderId,
            orderScriptId,
            deliverableId,
          ),
  });
}

export function useOrderScriptPreflightComments(
  orderId: string,
  orderScriptId: string,
  options: {
    audience: Exclude<OrderScriptActivityAudience, "partner">;
    tenantId: string;
    deliverableId?: string;
  },
  enabled = true,
) {
  return useQuery({
    ...orderScriptPreflightCommentsQueryOptions(
      orderId,
      orderScriptId,
      options,
    ),
    enabled,
  });
}

export function useSetOrderNoteReaction(
  audience: Exclude<OrderScriptActivityAudience, "partner">,
) {
  return useOrderNoteReactionMutation(
    audience,
    audience === "client"
      ? setClientOrderNoteReaction
      : setAdminOrderNoteReaction,
  );
}

export function useAddOrderScriptPreflightComment(
  audience: Exclude<OrderScriptActivityAudience, "partner"> = "admin",
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      orderId: string;
      orderScriptId: string;
      tenantId: string;
      body: string;
      requestRevision?: boolean;
      deliverableId?: string;
    }) =>
      audience === "client"
        ? addTenantOrderScriptPreflightComment(args.orderId, args.orderScriptId, {
            tenantId: args.tenantId,
            body: args.body,
            requestRevision: args.requestRevision,
            deliverableId: args.deliverableId,
          })
        : addOrderScriptPreflightComment(args.orderId, args.orderScriptId, {
            tenantId: args.tenantId,
            body: args.body,
            requestRevision: args.requestRevision,
            deliverableId: args.deliverableId,
          }),
    onSuccess: (data, args) => {
      invalidateOrderScriptPreflightCommentQueries(queryClient, {
        audiences: [audience],
        tenantScope: args.tenantId,
        orderId: args.orderId,
        orderScriptId: args.orderScriptId,
        deliverableId: args.deliverableId,
      });
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminOrdersRoot,
      });
      queryClient.invalidateQueries({
        queryKey: [...marketplaceKeys.all, "order-script-by-review"],
      });
      toast.success(
        args.requestRevision === false
          ? i18n.t("marketplace.orders.activity.commentSent", "Comment sent")
          : i18n.t(
              "marketplace.orders.preflightCommentSent",
              "Revision comment sent",
            ),
      );
    },
    onError: () => {
      toast.error(
        i18n.t(
          "marketplace.orders.preflightCommentError",
          "Couldn't send comment",
        ),
      );
    },
    meta: { skipGlobalError: true },
  });
}
